import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosConn from "config/axiosConn";
import ReactApexChart from "react-apexcharts";
import "./ThemesAnalyticsDashboard.css";
import "./ThemeDeepDive.css";

// ─── Constants ─────────────────────────────────────────────────────────────────
// Themes Analytics only shows data from June 5, 2026 onward.
const MIN_THEMES_DATE = "2026-06-05";

const THEME_ICONS = {
  "Adopt Healthy Lifestyles":       "🏃",
  "Adopt Sustainable Food Systems": "🌱",
  "Reduce Waste":                   "♻️",
  "Reduce E-Waste":                 "💻",
  "Save Energy":                    "⚡",
  "Save Water":                     "💧",
  "Say No to Single Use Plastic":   "🚫",
};

const THEME_COLORS = {
  "Adopt Healthy Lifestyles":       "#2563eb",
  "Adopt Sustainable Food Systems": "#0d9488",
  "Reduce Waste":                   "#ea580c",
  "Reduce E-Waste":                 "#7c3aed",
  "Save Energy":                    "#d97706",
  "Save Water":                     "#0891b2",
  "Say No to Single Use Plastic":   "#dc2626",
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) => {
  if (!n && n !== 0) return "—";
  if (n >= 1_000) return n.toLocaleString("en-IN");
  return n.toString();
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

// ─── Mini percentage bar cell ────────────────────────────────────────────────
function MiniBar({ value, max, color }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="tdd-mini-pct-bar">
      <div className="tdd-mini-pct-track">
        <div className="tdd-mini-pct-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="tdd-mini-pct-label">{pct.toFixed(0)}%</span>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function ThemeDeepDive() {
  const { themeName } = useParams();
  const navigate      = useNavigate();
  const userData      = useSelector((state) => state.auth.user);

  const decodedTheme = decodeURIComponent(themeName || "");
  const themeColor   = THEME_COLORS[decodedTheme] || "#3b82f6";
  const themeIcon    = THEME_ICONS[decodedTheme]  || "🌿";

  const isStateAdmin  = userData?.role === 4;
  const backPath      = isStateAdmin ? "/admin/state/themes-analysis" : "/admin/themes-analytics";

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [fromDate, setFromDate] = useState(MIN_THEMES_DATE);
  const [toDate, setToDate] = useState("");

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchDeepDive = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const encodedName = encodeURIComponent(decodedTheme);
      const payload = {};
      if (fromDate) payload.fromDate = fromDate + 'T00:00:00.000Z';
      if (toDate) payload.toDate = toDate + 'T23:59:59.999Z';
      const res = await axiosConn("post", `themes-dashboard/theme/${encodedName}`, payload);
      if (res?.data?.success) {
        setData(res.data);
      } else {
        setError("Failed to load theme data.");
      }
    } catch (err) {
      console.error("ThemeDeepDive fetch error:", err);
      setError(err?.response?.data?.error || "Network error — please retry.");
    } finally {
      setLoading(false);
    }
  }, [decodedTheme, fromDate, toDate]);

  useEffect(() => {
    if (decodedTheme) fetchDeepDive();
  }, [decodedTheme, fetchDeepDive]);

  // ── Sub-theme bar chart ───────────────────────────────────────────────────
  const subThemeChart = useMemo(() => {
    if (!data?.subThemes?.length) return null;
    const sorted = [...data.subThemes]
      .filter((s) => s.subTheme && s.subTheme !== "null")
      .sort((a, b) => b.registrations - a.registrations)
      .slice(0, 12);

    return {
      options: {
        chart: { type: "bar", background: "transparent", toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: "55%", borderRadius: 4 } },
        colors: [themeColor],
        xaxis: {
          categories: sorted.map((s) =>
            (s.subTheme?.length || 0) > 28
              ? s.subTheme.slice(0, 26) + "…"
              : (s.subTheme || "General")
          ),
          labels: { style: { colors: "#6c757d", fontSize: "10px", fontFamily: "Inter, sans-serif" } },
        },
        yaxis: { labels: { style: { colors: "#495057", fontSize: "10px", fontFamily: "Inter, sans-serif" } } },
        dataLabels: {
          enabled: true,
          formatter: fmt,
          style: { fontSize: "10px", fontFamily: "Inter, sans-serif", colors: ["#ffffff"] },
        },
        grid: { borderColor: "#e9ecef" },
        legend: { show: false },
        tooltip: { style: { fontFamily: "Inter, sans-serif" } },
        theme: { mode: "light" },
      },
      series: [{ name: "Events", data: sorted.map((s) => s.registrations) }],
    };
  }, [data, themeColor]);

  // ── Daily trend chart ─────────────────────────────────────────────────────
  const trendChart = useMemo(() => {
    if (!data?.trend?.length) return null;
    return {
      options: {
        chart: { type: "area", background: "transparent", toolbar: { show: false }, zoom: { enabled: false } },
        colors: [themeColor],
        fill: {
          type: "gradient",
          gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.03, stops: [0, 100] },
        },
        stroke: { curve: "smooth", width: 2 },
        xaxis: {
          categories: data.trend.map((d) => fmtDate(d._id)),
          labels: { style: { colors: "#6c757d", fontSize: "10px", fontFamily: "Inter, sans-serif" }, rotate: -30 },
          axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: "#6c757d", fontSize: "10px", fontFamily: "Inter, sans-serif" }, formatter: fmt } },
        grid: { borderColor: "#e9ecef", strokeDashArray: 4 },
        dataLabels: { enabled: false },
        tooltip: { style: { fontFamily: "Inter, sans-serif" } },
        theme: { mode: "light" },
      },
      series: [{ name: "Events", data: data.trend.map((d) => d.registrations) }],
    };
  }, [data, themeColor]);

  // ── Participants trend chart ─────────────────────────────────────────────
  const participantsTrendChart = useMemo(() => {
    if (!data?.trend?.length) return null;
    return {
      options: {
        chart: { type: "area", background: "transparent", toolbar: { show: false }, zoom: { enabled: false } },
        colors: ["#a78bfa"],
        fill: {
          type: "gradient",
          gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.03, stops: [0, 100] },
        },
        stroke: { curve: "smooth", width: 2 },
        xaxis: {
          categories: data.trend.map((d) => fmtDate(d._id)),
          labels: { style: { colors: "#6c757d", fontSize: "10px", fontFamily: "Inter, sans-serif" }, rotate: -30 },
          axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: "#6c757d", fontSize: "10px", fontFamily: "Inter, sans-serif" }, formatter: fmt } },
        grid: { borderColor: "#e9ecef", strokeDashArray: 4 },
        dataLabels: { enabled: false },
        tooltip: { style: { fontFamily: "Inter, sans-serif" } },
        theme: { mode: "light" },
      },
      series: [{ name: "Participants", data: data.trend.map((d) => d.participants || 0) }],
    };
  }, [data]);

  // ── Derived max values for mini bars ──────────────────────────────────────
  const maxStateRegs    = Math.max(...(data?.topStates   ?.map((s) => s.registrations) || [0]));
  const maxDistrictRegs = Math.max(...(data?.topDistricts?.map((d) => d.registrations) || [0]));
  const maxSchoolRegs   = Math.max(...(data?.topSchools  ?.map((s) => s.registrations) || [0]));

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div
      className="tad-page"
      style={{
        "--theme-color":    themeColor,
        "--theme-hero-bg":  themeColor + "18",
      }}
    >
      {/* ── HERO ── */}
      <div className="tdd-hero">
        <div className="tdd-breadcrumb">
          <button onClick={() => navigate(backPath)}>
            Themes Dashboard
          </button>
          <span>›</span>
          <span style={{ color: "#94a3b8" }}>{decodedTheme}</span>
        </div>

        <div className="tdd-hero-content">
          <div className="tdd-hero-left">
            <h1 className="tdd-hero-title">{decodedTheme}</h1>
            {/* <p className="tdd-hero-sub">
              <span
                className="tdd-hero-color-dot"
                style={{ background: themeColor }}
              />
              Mission LiFE · Theme Deep Dive
            </p> */}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <label style={{ fontSize: "10px", fontWeight: "600", color: "#64748b", marginBottom: "2px" }}>From Date</label>
              <input
                type="date"
                value={fromDate}
                min={MIN_THEMES_DATE}
                onChange={(e) => setFromDate(e.target.value)}
                style={{
                  padding: "5px 10px",
                  borderRadius: "6px",
                  border: "1.5px solid #e2e8f0",
                  fontSize: "12px",
                  outline: "none",
                  color: "#334155"
                }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <label style={{ fontSize: "10px", fontWeight: "600", color: "#64748b", marginBottom: "2px" }}>To Date</label>
              <input
                type="date"
                value={toDate}
                min={MIN_THEMES_DATE}
                onChange={(e) => setToDate(e.target.value)}
                style={{
                  padding: "5px 10px",
                  borderRadius: "6px",
                  border: "1.5px solid #e2e8f0",
                  fontSize: "12px",
                  outline: "none",
                  color: "#334155"
                }}
              />
            </div>
            {(fromDate !== MIN_THEMES_DATE || toDate) && (
              <button
                onClick={() => {
                  setFromDate(MIN_THEMES_DATE);
                  setToDate("");
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#dc2626",
                  fontSize: "11px",
                  fontWeight: "700",
                  cursor: "pointer",
                  marginTop: "14px",
                  padding: "0"
                }}
              >
                Clear
              </button>
            )}
            <button
              className="tad-btn tad-btn-ghost"
              onClick={() => navigate(backPath)}
              style={{ alignSelf: "flex-start" }}
            >
              ← All Themes
            </button>
          </div>
        </div>

        {/* KPI Chips */}
        {data?.kpis && (
          <div className="tdd-kpi-strip">
            <div className="tdd-kpi-chip">
              <span className="tdd-kpi-chip-val">{fmt(data.kpis.registrations)}</span>
              <span className="tdd-kpi-chip-lbl">Events</span>
            </div>
            <div className="tdd-kpi-chip">
              <span className="tdd-kpi-chip-val">{fmt(data.kpis.schools)}</span>
              <span className="tdd-kpi-chip-lbl">Schools</span>
            </div>
            <div className="tdd-kpi-chip">
              <span className="tdd-kpi-chip-val">{fmt(data.kpis.districts)}</span>
              <span className="tdd-kpi-chip-lbl">Districts</span>
            </div>
            <div className="tdd-kpi-chip">
              <span className="tdd-kpi-chip-val">{fmt(data.kpis.states)}</span>
              <span className="tdd-kpi-chip-lbl">States/UTs/ABs</span>
            </div>
            <div className="tdd-kpi-chip">
              <span className="tdd-kpi-chip-val">{fmt(data.kpis.participants)}</span>
              <span className="tdd-kpi-chip-lbl">Participants</span>
            </div>
            {decodedTheme === "Adopt Healthy Lifestyles" ? (
              <div className="tdd-kpi-chip">
                <span className="tdd-kpi-chip-val">{fmt(data.kpis.saplings_planted)}</span>
                <span className="tdd-kpi-chip-lbl">Total No. of saplings planted</span>
              </div>
            ) : (
              <div className="tdd-kpi-chip">
                <span className="tdd-kpi-chip-val">{fmt(data.kpis.events)}</span>
                <span className="tdd-kpi-chip-lbl">Activities</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── BODY ── */}
      <div className="tdd-body">
        {loading && (
          <div className="tdd-loading">
            <div className="tdd-spinner" />
            <span className="tdd-loading-text">Loading theme data…</span>
          </div>
        )}

        {!loading && error && (
          <div className="tdd-error">
            <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Error loading data</div>
            <div style={{ fontSize: 13, opacity: 0.8 }}>{error}</div>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* ── TREND CHARTS ── */}
            <div className="tad-section-title">📅 Event & Participant Trend</div>
            <div className="tdd-two-col" style={{ marginBottom: 28 }}>
              {trendChart && (
                <div className="tad-chart-card">
                  <div className="tad-chart-title">📋 Daily Events</div>
                  <ReactApexChart
                    options={trendChart.options}
                    series={trendChart.series}
                    type="area"
                    height={200}
                  />
                </div>
              )}
              {participantsTrendChart && (
                <div className="tad-chart-card">
                  <div className="tad-chart-title">👥 Daily Participants</div>
                  <ReactApexChart
                    options={participantsTrendChart.options}
                    series={participantsTrendChart.series}
                    type="area"
                    height={200}
                  />
                </div>
              )}
            </div>

            {/* ── SUB-THEMES ── */}
            {data.subThemes?.length > 0 && (
              <>
                <div className="tad-section-title">🔍 Sub-Theme Breakdown</div>
                <div className="tdd-two-col" style={{ marginBottom: 28 }}>
                  {/* Chart */}
                  {subThemeChart && (
                    <div className="tad-chart-card">
                      <div className="tad-chart-title">Sub-Theme Events</div>
                      <ReactApexChart
                        options={subThemeChart.options}
                        series={subThemeChart.series}
                        type="bar"
                        height={Math.max(260, data.subThemes.length * 28)}
                      />
                    </div>
                  )}
                  {/* Table */}
                  <div className="tdd-panel">
                    <div className="tdd-panel-header">
                      <span className="tdd-panel-title">Sub-Theme Leaderboard</span>
                      <span className="tdd-panel-count">{data.subThemes.length} sub-themes</span>
                    </div>
                    <table className="tdd-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Sub-Theme</th>
                          <th>Events</th>
                          <th>Schools</th>
                          <th>Share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.subThemes
                          .filter((s) => s.subTheme && s.subTheme !== "null")
                          .sort((a, b) => b.registrations - a.registrations)
                          .map((s, idx) => {
                            const maxR = data.subThemes[0]?.registrations || 1;
                            return (
                              <tr key={idx}>
                                <td style={{ color: "#6c757d" }}>{idx + 1}</td>
                                <td style={{ fontWeight: 500, color: "#495057", maxWidth: 160 }}>
                                  {s.subTheme || "General"}
                                </td>
                                <td>{fmt(s.registrations)}</td>
                                <td>{fmt(s.schools)}</td>
                                <td>
                                  <MiniBar value={s.registrations} max={maxR} color={themeColor} />
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* ── TOP STATES + DISTRICTS ── */}
            <div className="tad-section-title">🗺️ Geographic Breakdown</div>
            <div className="tdd-two-col" style={{ marginBottom: 28 }}>
              {/* Top States */}
              <div className="tdd-panel">
                <div className="tdd-panel-header">
                  <span className="tdd-panel-title">🏆 Top 10 States/UTs/ABs</span>
                  <span className="tdd-panel-count">{data.topStates?.length} states</span>
                </div>
                {data.topStates?.length > 0 ? (
                  <table className="tdd-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>States/UTs/ABs</th>
                        <th>Events</th>
                        <th>Schools</th>
                        <th>Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.topStates.map((s, idx) => (
                        <tr key={idx}>
                          <td>
                            <span style={{
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              width: 22, height: 22, borderRadius: 6, fontSize: 11, fontWeight: 800,
                              background: idx < 3 ? themeColor : "rgba(0,0,0,0.05)",
                              color: idx < 3 ? "white" : "#6c757d",
                            }}>
                              {idx + 1}
                            </span>
                          </td>
                          <td style={{ fontWeight: 600, color: "#212529" }}>{s.state}</td>
                          <td>{fmt(s.registrations)}</td>
                          <td>{fmt(s.schools)}</td>
                          <td>
                            <MiniBar value={s.registrations} max={maxStateRegs} color={themeColor} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="tdd-empty">
                    <div className="tdd-empty-icon">🗺️</div>
                    No state data available
                  </div>
                )}
              </div>

              {/* Top Districts */}
              <div className="tdd-panel">
                <div className="tdd-panel-header">
                  <span className="tdd-panel-title">🏙️ Top 10 Districts</span>
                  <span className="tdd-panel-count">{data.topDistricts?.length} districts</span>
                </div>
                {data.topDistricts?.length > 0 ? (
                  <table className="tdd-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>District</th>
                        <th>Events</th>
                        <th>Schools</th>
                        <th>Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.topDistricts.map((d, idx) => (
                        <tr key={idx}>
                          <td style={{ color: "#6c757d" }}>{idx + 1}</td>
                          <td style={{ fontWeight: 500, color: "#495057" }}>{d.district}</td>
                          <td>{fmt(d.registrations)}</td>
                          <td>{fmt(d.schools)}</td>
                          <td>
                            <MiniBar value={d.registrations} max={maxDistrictRegs} color={themeColor} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="tdd-empty">
                    <div className="tdd-empty-icon">🏙️</div>
                    No district data available
                  </div>
                )}
              </div>
            </div>

          </>
        )}
      </div>
    </div>
  );
}
